import { Money } from '@principle-theorem/accounting';
import {
  DEPOSIT_CAPTURE_STRATEGY_TO_PAYMENT_MODE_MAP,
  DepositCaptureStrategy,
  DepositStatus,
  isUnpaidDepositStatus,
  isTreatmentStep,
  isTreatmentStepFromTemplate,
  type DepositPaymentMode,
  type IAppointmentDeposit,
  type IAppointmentRequest,
  type IDepositMetadata,
  type IEvent,
  type ITreatmentStep,
} from '@principle-theorem/principle-core/interfaces';
import { type WithRef } from '@principle-theorem/shared';
import { omit } from 'lodash';
import { combineLatest, type Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { type IAppointmentDetails } from '../appointment-store/appointment-details';
import { TreatmentStep } from '../clinical-charting/treatment/treatment-step';
import { resolveEffectiveCaptureStrategy } from './effective-capture-strategy';

/**
 * Inputs required to derive a shared `canSave$` observable for the staff-create
 * flows (create-appointment page + appointment-create-sidebar).
 *
 * `formValid$` is the caller-composed validity signal — each entry point folds
 * its own required-field checks (patient, treatment, reschedule reason, etc.)
 * into this single boolean so the shared helper only concerns itself with the
 * saving/deposit gate.
 */
export interface ICanSaveInputs {
  savingAppointment$: Observable<boolean>;
  formValid$: Observable<boolean>;
  depositRequirement$: Observable<DepositRequirementState>;
  depositMetadata$: Observable<IDepositMetadata | undefined>;
  depositAmount$?: Observable<number | undefined>;
  depositRequiredOnlyBecauseStaffOptedIn$?: Observable<boolean>;
}

/**
 * Whether a deposit will hold the card or charge it for one booking. Both
 * fields are omitted when no capture strategy is configured.
 */
export interface IEffectiveDepositCapture {
  captureStrategy?: DepositCaptureStrategy;
  paymentMode?: DepositPaymentMode;
}

export enum DepositRequirementState {
  Resolving = 'resolving',
  NotRequired = 'notRequired',
  Required = 'required',
}

export const DEPOSIT_REQUIREMENT_RESOLVING_MESSAGE =
  'Checking deposit requirements before saving.';
const STAFF_OPT_IN_DEPOSIT_AMOUNT_REQUIRED_MESSAGE =
  'Enter a deposit amount, or turn the deposit off for this appointment.';
const STANDARD_SAVE_LABEL = 'Save Appointment';
const TAKE_DEPOSIT_SAVE_LABEL = 'Save Appointment & Take Deposit';
const CHARGE_DEPOSIT_SAVE_LABEL = 'Save Appointment & Charge Deposit';
const FORFEIT_DEPOSIT_SAVE_LABEL = 'Save Appointment & Forfeit Deposit';

/**
 * Centralises appointment-deposit calculations and save gates so every
 * staff-create entry point applies the same deposit requirement semantics.
 */
export class AppointmentDeposit {
  /**
   * Limits the client-side waiver action to deposit states that can transition
   * into a waiver.
   */
  static canWaive(deposit?: IAppointmentDeposit): boolean {
    return (
      deposit !== undefined &&
      [
        DepositStatus.Required,
        DepositStatus.Pending,
        DepositStatus.Authorised,
      ].includes(deposit.metadata.status)
    );
  }

  /**
   * Reinstates a waived requirement while preserving the deposit's financial
   * configuration and removing fields that apply only to a waiver.
   */
  static reinstate(
    deposit?: IAppointmentDeposit
  ): IAppointmentDeposit | undefined {
    if (deposit?.metadata.status !== DepositStatus.Waived) {
      return;
    }
    const metadata = omit(deposit.metadata, ['waivedBy', 'waiverReason']);
    return {
      ...deposit,
      metadata: {
        ...metadata,
        status: DepositStatus.Required,
      },
    };
  }

  /**
   * Keeps treatment pricing in cents consistent across staff-create flows,
   * including the template override fallback required for percentage deposits.
   */
  static getTreatmentCost(
    appointmentDetails: IAppointmentDetails
  ): number | undefined {
    const treatmentStep =
      AppointmentDeposit._selectedTreatmentStep(appointmentDetails);
    if (!treatmentStep) {
      return;
    }

    const price = TreatmentStep.price(treatmentStep);
    if (price > 0) {
      return Money.toCents(price);
    }

    // Fallback: priceOverride from template's enabled practice (already in cents).
    if (isTreatmentStepFromTemplate(treatmentStep)) {
      const enabledPractices = treatmentStep.template.enabledPractices;
      const selectedPracticeId = appointmentDetails.practice?.ref.id;
      if (selectedPracticeId && enabledPractices) {
        const enabledPractice = enabledPractices.find(
          (ep) => ep.practice.ref.id === selectedPracticeId
        );
        if (enabledPractice?.priceOverride !== undefined) {
          return enabledPractice.priceOverride;
        }
      }
    }

    return;
  }

  /**
   * Limits rule lookup to template-backed treatments because only those have a
   * template-specific deposit rule.
   */
  static getTreatmentTemplateId(
    appointmentDetails: IAppointmentDetails
  ): string | undefined {
    const treatmentStep = appointmentDetails.treatment?.step;
    if (!treatmentStep || !isTreatmentStepFromTemplate(treatmentStep)) {
      return;
    }
    return treatmentStep.template.ref.id;
  }

  /**
   * Preserves every selected treatment configuration for deposit calculation,
   * including rules that apply below the treatment template level.
   */
  static getTreatmentConfigurationIds(
    appointmentDetails: IAppointmentDetails
  ): string[] {
    return (
      AppointmentDeposit._selectedTreatmentStep(
        appointmentDetails
      )?.treatments.map((treatment) => treatment.config.ref.id) ?? []
    );
  }

  /**
   * Carries the names alongside the ids the deposit rules match on. Only the
   * ids reach the resolver, so without this the UI can say a deposit came from
   * a treatment configuration but never which one.
   */
  static getTreatmentConfigurationNames(
    appointmentDetails: IAppointmentDetails
  ): Record<string, string> {
    const treatments =
      AppointmentDeposit._selectedTreatmentStep(appointmentDetails)
        ?.treatments ?? [];
    return treatments.reduce<Record<string, string>>(
      (names, treatment) => ({
        ...names,
        [treatment.config.ref.id]: treatment.config.name,
      }),
      {}
    );
  }

  static getTreatmentTemplateName(
    appointmentDetails: IAppointmentDetails
  ): string | undefined {
    const treatmentStep = appointmentDetails.treatment?.step;
    if (!treatmentStep || !isTreatmentStepFromTemplate(treatmentStep)) {
      return;
    }
    return treatmentStep.template.name;
  }

  /**
   * Provides one save gate so staff-create entry points cannot drift on when a
   * resolved deposit permits the appointment to be saved.
   *
   * The gate resolves to `true` when:
   *   - not currently saving,
   *   - the caller-provided form validity signal is `true`,
   *   - the deposit requirement has finished resolving, and
   *   - a staff-added deposit has an amount to take.
   *
   * The last condition covers only the opt-in case. A deposit the resolver
   * required but could not price still saves, carrying its warning: that
   * outcome is the settings failing to produce an amount, not a staff member
   * leaving a field blank, and blocking it would strand the booking.
   *
   * @remarks Both staff-create entry points share this single gate to avoid
   * per-site drift on save-disable semantics (Refs: B4).
   */
  static getCanSave$(inputs: ICanSaveInputs): Observable<boolean> {
    return combineLatest([
      inputs.savingAppointment$,
      inputs.formValid$,
      inputs.depositRequirement$,
      inputs.depositMetadata$,
      inputs.depositAmount$ ?? of(undefined),
      inputs.depositRequiredOnlyBecauseStaffOptedIn$ ?? of(false),
    ]).pipe(
      map(
        ([
          saving,
          formValid,
          depositRequirement,
          depositMetadata,
          depositAmount,
          depositRequiredOnlyBecauseStaffOptedIn,
        ]) =>
          !saving &&
          formValid &&
          depositRequirement !== DepositRequirementState.Resolving &&
          !AppointmentDeposit._staffOptInAmountIsMissing({
            depositMetadata,
            depositAmount,
            depositRequiredOnlyBecauseStaffOptedIn,
          })
      )
    );
  }

  /**
   * Explains a deposit-specific save block so staff can resolve the condition
   * instead of seeing a generic disabled save action.
   */
  static getDepositSaveDisabledMessage$(inputs: {
    depositRequirement$: Observable<DepositRequirementState>;
    depositMetadata$: Observable<IDepositMetadata | undefined>;
    depositAmount$?: Observable<number | undefined>;
    depositRequiredOnlyBecauseStaffOptedIn$?: Observable<boolean>;
  }): Observable<string | undefined> {
    return combineLatest([
      inputs.depositRequirement$,
      inputs.depositMetadata$,
      inputs.depositAmount$ ?? of(undefined),
      inputs.depositRequiredOnlyBecauseStaffOptedIn$ ?? of(false),
    ]).pipe(
      map(
        ([
          depositRequirement,
          depositMetadata,
          depositAmount,
          depositRequiredOnlyBecauseStaffOptedIn,
        ]) => {
          switch (depositRequirement) {
            case DepositRequirementState.Resolving:
              return DEPOSIT_REQUIREMENT_RESOLVING_MESSAGE;
            case DepositRequirementState.Required:
              return AppointmentDeposit._staffOptInAmountIsMissing({
                depositMetadata,
                depositAmount,
                depositRequiredOnlyBecauseStaffOptedIn,
              })
                ? STAFF_OPT_IN_DEPOSIT_AMOUNT_REQUIRED_MESSAGE
                : undefined;
            case DepositRequirementState.NotRequired:
            default:
              return;
          }
        }
      )
    );
  }

  /**
   * Says on the button what saving will do to the deposit, so nobody charges
   * a patient's card or is handed a capture panel they were not expecting.
   *
   * A held card that the accept step captures reads "charge": the hold was
   * taken with the booking request and approving the request is what charges
   * it. A deposit still owed reads "take", since the capture panel opens after
   * save. `selectedEvent$` gates the latter because capture runs in a timeline
   * sidebar, which an appointment with no event cannot open. Both staff-create
   * surfaces allow saving unscheduled, so without this the button offered to
   * take a deposit and then silently could not.
   */
  static getSaveButtonLabel$(inputs: {
    depositRequirement$: Observable<DepositRequirementState>;
    depositMetadata$: Observable<IDepositMetadata | undefined>;
    selectedEvent$: Observable<IEvent | undefined>;
    /**
     * Whether the selected reschedule reason forfeits the existing deposit.
     * Optional so surfaces with no reschedule reason are unaffected.
     */
    willForfeitDeposit$?: Observable<boolean>;
  }): Observable<string> {
    return combineLatest([
      inputs.depositRequirement$,
      inputs.depositMetadata$,
      inputs.selectedEvent$,
      inputs.willForfeitDeposit$ ?? of(false),
    ]).pipe(
      map(
        ([
          depositRequirement,
          depositMetadata,
          selectedEvent,
          willForfeitDeposit,
        ]) => {
          // Forfeiting takes precedence: it is what happens to the deposit
          // the patient has already paid or had held, which outranks taking
          // a new one for the rebooked appointment.
          if (willForfeitDeposit) {
            return FORFEIT_DEPOSIT_SAVE_LABEL;
          }
          if (
            AppointmentDeposit._willChargeHeldDepositOnSave(depositMetadata)
          ) {
            return CHARGE_DEPOSIT_SAVE_LABEL;
          }
          return AppointmentDeposit._willTakeDepositOnSave(
            depositRequirement,
            depositMetadata,
            selectedEvent
          )
            ? TAKE_DEPOSIT_SAVE_LABEL
            : STANDARD_SAVE_LABEL;
        }
      )
    );
  }

  /**
   * Works out whether a configured deposit will hold the card or charge it for
   * this booking, the same way the server creates the deposit request. A hold
   * until approval has nothing to approve on a staff booking, and cancellation
   * cover cannot hold a card for a booking too far ahead, so both charge up
   * front. Every pre-save surface uses this so the icon staff see before saving
   * matches what the deposit will actually do.
   */
  static getEffectiveCapture(inputs: {
    captureStrategy?: DepositCaptureStrategy;
    appointmentStart?: IEvent['from'];
    hasApprovableBookingRequest: boolean;
  }): IEffectiveDepositCapture {
    if (!inputs.captureStrategy) {
      return {};
    }
    const { strategy } = resolveEffectiveCaptureStrategy({
      requestedStrategy: inputs.captureStrategy,
      appointmentStart: inputs.appointmentStart,
      hasApprovableBookingRequest: inputs.hasApprovableBookingRequest,
    });
    return {
      captureStrategy: strategy,
      paymentMode: DEPOSIT_CAPTURE_STRATEGY_TO_PAYMENT_MODE_MAP[strategy],
    };
  }

  /**
   * Keeps the effective capture live on the scheduling form, where the chosen
   * time and whether a booking request is being accepted can both change.
   */
  static getEffectiveCapture$(inputs: {
    captureStrategy$: Observable<DepositCaptureStrategy | undefined>;
    selectedEvent$: Observable<IEvent | undefined>;
    appointmentRequest$: Observable<WithRef<IAppointmentRequest> | undefined>;
  }): Observable<IEffectiveDepositCapture> {
    return combineLatest([
      inputs.captureStrategy$,
      inputs.selectedEvent$,
      inputs.appointmentRequest$,
    ]).pipe(
      map(([captureStrategy, selectedEvent, appointmentRequest]) =>
        AppointmentDeposit.getEffectiveCapture({
          captureStrategy,
          appointmentStart: selectedEvent?.from,
          hasApprovableBookingRequest: !!appointmentRequest,
        })
      )
    );
  }

  /**
   * A hold taken with a booking request under "charge on accept" is captured
   * by the request approval that follows the save. A hold kept as cancellation
   * cover stays a hold, so it is not a charge and gets the standard label.
   */
  private static _willChargeHeldDepositOnSave(
    depositMetadata?: IDepositMetadata
  ): boolean {
    return (
      depositMetadata?.status === DepositStatus.Authorised &&
      depositMetadata.captureStrategy ===
        DepositCaptureStrategy.CaptureOnBookingRequestApproval
    );
  }

  private static _willTakeDepositOnSave(
    depositRequirement: DepositRequirementState,
    depositMetadata?: IDepositMetadata,
    selectedEvent?: IEvent
  ): boolean {
    if (!selectedEvent) {
      return false;
    }
    if (depositRequirement !== DepositRequirementState.Required) {
      return false;
    }
    return (
      depositMetadata === undefined ||
      isUnpaidDepositStatus(depositMetadata.status)
    );
  }

  private static _staffOptInAmountIsMissing(inputs: {
    depositMetadata?: IDepositMetadata;
    depositAmount?: number;
    depositRequiredOnlyBecauseStaffOptedIn: boolean;
  }): boolean {
    return (
      inputs.depositRequiredOnlyBecauseStaffOptedIn &&
      !inputs.depositMetadata &&
      (inputs.depositAmount === undefined || inputs.depositAmount <= 0)
    );
  }

  /**
   * The appointment details form searches treatments in its treatment field,
   * so until an option is picked the details hold the search text rather than
   * a treatment.
   */
  private static _selectedTreatmentStep(
    appointmentDetails: IAppointmentDetails
  ): ITreatmentStep | undefined {
    const treatmentStep = appointmentDetails.treatment?.step;
    return isTreatmentStep(treatmentStep) ? treatmentStep : undefined;
  }
}
